import { isClerkAPIResponseError } from '@clerk/clerk-react/errors';
import { faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter, useSearch } from '@tanstack/react-router';
import {
	getGetAllShipmentMetaDataQueryKey,
	getMetaDataQueryKey,
	UserRole,
} from '@uturn/api/v1';
import { queryClient } from '@uturn/portal/query';
import {
	Button,
	cn,
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	Icon,
	Input,
	sonner,
} from '@uturn/ui-kit';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks';
import { type SignInFormValues, signInSchema } from '../schema';

const FORBIDDEN_ROLES = [
	UserRole.G_CARRIER,
	UserRole.C_USER,
	UserRole.C_ADMIN,
	UserRole.U_ADMIN,
	UserRole.C_DRIVER,
] as unknown as UserRole[];

export function SignInForm() {
	const { t } = useTranslation();
	const { login, logout } = useAuth();
	const router = useRouter();
	const { email, redirect } = useSearch({ from: '/_auth/sign-in' });

	const form = useForm<SignInFormValues>({
		resolver: zodResolver(signInSchema),
		mode: 'onTouched',
		defaultValues: {
			username: email ?? '',
			password: '',
		},
	});

	const onSubmit = async (data: SignInFormValues) => {
		try {
			const user = await login(data);

			if (!user) {
				sonner.error(
					t(
						'pages.sign-in.form.submit.error.description',
						'Please check your credentials and try again.',
					),
				);
				return;
			}

			// Redirect to old shipper platform if the user has incorrect roles
			if (user.role && FORBIDDEN_ROLES.includes(user.role)) {
				await logout();
				window.location.replace(import.meta.env.VITE_CARRIER_PORTAL_URL);
				return;
			}

			// Force refetch of useMetaData, useGetAllShipmentMetaData
			// after authentication in AppLayout.
			queryClient.invalidateQueries({
				queryKey: [getMetaDataQueryKey(), getGetAllShipmentMetaDataQueryKey()],
			});

			// Check if redirect exists in search
			if (redirect) {
				router.navigate({ to: redirect });
				return;
			}

			// Else just redirect to shipments.
			router.navigate({ to: '/shipments' });
		} catch (error) {
			if (isClerkAPIResponseError(error)) {
				const code = error.errors[0]?.code;
				if (
					code === 'form_identifier_not_found' ||
					code === 'form_password_incorrect'
				) {
					sonner.error(
						t(
							'pages.sign-in.form.submit.error.invalid-credentials',
							'Invalid email or password. Please try again.',
						),
					);
					return;
				}
			}
			sonner.error(
				t(
					'pages.sign-in.form.submit.error.description',
					'Please check your credentials and try again.',
				),
			);
		}
	};

	return (
		<div className={cn('grid gap-6')}>
			<Form {...form}>
				<form onSubmit={form.handleSubmit(onSubmit)}>
					<div className="grid gap-2">
						<div className="grid gap-1">
							<FormField
								control={form.control}
								name="username"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="sr-only">
											{t('pages.sign-in.form.username.label', 'Email')}
										</FormLabel>
										<FormControl>
											<Input
												type="email"
												autoComplete="email"
												autoCorrect="off"
												disabled={form.formState.isSubmitting}
												placeholder={t(
													'pages.sign-in.form.username.placeholder',
													'john_doe@example.com',
												).toString()}
												{...field}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div className="grid gap-1">
							<FormField
								control={form.control}
								name="password"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="sr-only">
											{t('pages.sign-in.form.password.label', 'Password')}
										</FormLabel>
										<FormControl>
											<Input
												type="password"
												autoComplete="current-password"
												autoCorrect="off"
												disabled={form.formState.isSubmitting}
												placeholder={t(
													'pages.sign-in.form.password.placeholder',
													'Password',
												).toString()}
												{...field}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<Button
							type="submit"
							disabled={form.formState.isSubmitting || !form.formState.isValid}
						>
							{form.formState.isSubmitting && (
								<Icon spin className="mr-3" icon={faSpinnerThird} />
							)}
							{t(
								'pages.auth.sign-in.form.submit',
								t('pages.sign-in.sign-in', 'Sign In'),
							)}
						</Button>
					</div>
				</form>
			</Form>
		</div>
	);
}
